import React, { useEffect, useState } from "react";
import { Star } from "lucide-react";
import { useBluelagoon } from "@bluelagoon";
import { cn } from "@utils";

const FavoriteButton = ({
    workflowId,
    workflowPublicKey,
    isFavorite: initialIsFavorite = false,
    onFavoriteChange,
    className,
    size = 16,
}) => {
    const [isFavorite, setIsFavorite] = useState(initialIsFavorite);
    const [isLoading, setIsLoading] = useState(false);

    // Sync the prop into local state so this stays correct after
    // useFavorites() finishes loading or when the parent reloads. Local
    // state is still kept so the click handler can flip optimistically
    // before the API roundtrip resolves.
    useEffect(() => {
        setIsFavorite(initialIsFavorite);
    }, [initialIsFavorite]);

    const { call: addFavorite } = useBluelagoon({
        method: "addUserFavorite",
    });

    const { call: removeFavorite } = useBluelagoon({
        method: "removeUserFavorite",
    });

    const handleToggleFavorite = async (e) => {
        // stopPropagation alone leaves the click bubbling up to enclosing
        // <a>/<InternalLink> elements (the card overlay case), where the
        // browser still fires the anchor's default navigation. preventDefault
        // suppresses that, so a click on the heart only toggles the favorite.
        e.preventDefault();
        e.stopPropagation();

        if (isLoading) return;

        setIsLoading(true);

        try {
            if (isFavorite) {
                await removeFavorite({
                    workflowId: workflowId || workflowPublicKey,
                });
                setIsFavorite(false);
                onFavoriteChange?.(false);
            } else {
                await addFavorite({
                    workflowId: workflowId || workflowPublicKey,
                });
                setIsFavorite(true);
                onFavoriteChange?.(true);
            }
        } catch (error) {
            console.error("Error toggling favorite:", error);
            // Revert the state on error
            // (The state will be reverted automatically since we didn't update it yet)
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <button
            type="button"
            onClick={handleToggleFavorite}
            disabled={isLoading}
            className={cn(
                "p-1 rounded-full transition-colors duration-200",
                "hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-gray-200",
                "disabled:opacity-50 disabled:cursor-not-allowed",
                className
            )}
            aria-label={
                isFavorite ? "Remove from favorites" : "Add to favorites"
            }
        >
            <Star
                size={size}
                className={cn(
                    "transition-colors duration-200",
                    isFavorite
                        ? "fill-yellow-400 text-yellow-400"
                        : "text-gray-400 hover:text-yellow-400"
                )}
            />
        </button>
    );
};

export default FavoriteButton;
